import { OptionItem } from '@app/components/ui/select/select.type';
import {
  StatisticWidgetsBulkRequestType,
  StatisticWidgetsRangeType,
} from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';
import ChartDatePickerModalBlock from '@app/pages/reports/blocks/chart-date-picker-modal.block';
import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';
import { Select, FormTabSwitcher } from '@ui';
import React, { FC, useState } from 'react';

import useTranslation from '@app/hooks/use-translation';

import RangeBlock from './range-block';

const QM_STATISTIC_RANGE_STORAGE_KEY = 'QM_STATISTIC_RANGE_STORAGE';

export const StatisticWidgetsFilterBlock: FC<{
  widgetsFilterBlock: Partial<StatisticWidgetsBulkRequestType>;
  changeWidgetsFilterBlock: React.Dispatch<
    React.SetStateAction<Partial<StatisticWidgetsBulkRequestType>>
  >;
  teamsOptions: OptionItem[];
  setOperatorOptions: React.Dispatch<React.SetStateAction<OptionItem[]>>;
  operatorOptions: OptionItem[];
  getOperatorsByTeams: (teams: string[]) => Promise<OptionItem[]>;
}> = (props) => {
  const { t } = useTranslation('pages.qmManage.statistic');

  const rangeFromStor = localStorage.getItem(QM_STATISTIC_RANGE_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(QM_STATISTIC_RANGE_STORAGE_KEY) || '')
    : (undefined as StatisticWidgetsRangeType | undefined);
  const { data: userInfo } = useGetUserInfoQuery();
  const { qm_role = 'operator' } = userInfo || {};
  const {
    changeWidgetsFilterBlock,
    widgetsFilterBlock,
    teamsOptions,
    getOperatorsByTeams,
    operatorOptions,
    setOperatorOptions,
  } = props;
  const [isShowModelDatePicker, changeShowingModelDatePicker] = useState<boolean>(false);

  function handlerChangeCustomRangeInterval({ start, end }) {
    localStorage.setItem(
      QM_STATISTIC_RANGE_STORAGE_KEY,
      JSON.stringify({
        type: 'r',
        parameters: [
          { condition: '>', value: start },
          { condition: '<', value: end },
        ],
      }),
    );

    changeWidgetsFilterBlock((prev) => {
      return {
        ...prev,
        range: {
          dt_type: prev.range?.dt_type,
          type: 'r',
          parameters: [
            { condition: '>', value: start },
            { condition: '<', value: end },
          ],
        },
      };
    });
  }

  function handlerDateDropTrigger(key: StatisticWidgetsRangeType['type']) {
    if (key === 'r') {
      changeShowingModelDatePicker(true);
      return;
    }
    localStorage.setItem(
      QM_STATISTIC_RANGE_STORAGE_KEY,
      JSON.stringify({ type: key, parameters: [] }),
    );
    changeWidgetsFilterBlock((prev) => ({
      ...prev,
      range: { dt_type: prev.range?.dt_type, type: key, parameters: [] },
    }));
  }

  function handleChangeDtType(value: string) {
    changeWidgetsFilterBlock((prev) => {
      if (prev.range?.dt_type === value) return prev;
      const val: StatisticWidgetsBulkRequestType = {
        ...prev,
        range: {
          ...prev.range,
          dt_type: value as 'start' | 'processing',
        },
      };
      return val;
    });
  }

  function onChangeSelect(values: string[], key: 'operators' | 'teams') {
    changeWidgetsFilterBlock((prev) => ({ ...prev, [key]: values }));
    if (key === 'operators') return;
    if (!values.length) {
      setOperatorOptions([]);
      return;
    }
    getOperatorsByTeams(values).then((op) => setOperatorOptions(op));
  }

  const dtTypeValuesList = [
    { value: 'start', title: t('dt_type.start') },
    { value: 'processing', title: t('dt_type.processing') },
  ];

  return (
    <div
      className={`w-full grid grid-rows-1 ${
        userInfo?.qm_role === 'operator' ? 'grid-cols-2' : 'grid-cols-3'
      } gap-x-[20px] `}
    >
      {qm_role !== 'operator' && (
        <div className="flex items-center gap-[20px]  w-full">
          <div className="basis-1/2 max-w-[50%] ">
            <Select
              placeholder={t('filter.teams_placeholder')}
              options={teamsOptions}
              defaultValue={widgetsFilterBlock.teams || []}
              isMulti
              onChangeMultiple={(values) => onChangeSelect(values as string[], 'teams')}
            />
          </div>
          <div className="basis-1/2 max-w-[50%]">
            <Select
              disable={!widgetsFilterBlock.teams || !operatorOptions.length}
              placeholder={t('filter.operators_placeholder')}
              defaultValue={widgetsFilterBlock.operators || []}
              options={operatorOptions}
              isMulti
              onChangeMultiple={(values) => onChangeSelect(values as string[], 'operators')}
            />
          </div>
        </div>
      )}
      <div className="w-full">
        <FormTabSwitcher
          classname="!bg-[#efefef]"
          valuesList={dtTypeValuesList}
          isActive={widgetsFilterBlock.range?.dt_type || 'start'}
          onChange={handleChangeDtType}
        />
      </div>
      <div className="min-w-[320px] flex justify-end">
        <RangeBlock
          rangeFromStor={rangeFromStor}
          keysToHide={['n']}
          activeKey={widgetsFilterBlock.range?.type}
          onChange={(key) => handlerDateDropTrigger(key as StatisticWidgetsRangeType['type'])}
        />
      </div>
      <ChartDatePickerModalBlock
        dataIdStartDate="statistic-interval-date-from"
        dataIdEndDate="statistic-interval-date-to"
        onSuccess={handlerChangeCustomRangeInterval}
        show={isShowModelDatePicker}
        onClose={changeShowingModelDatePicker.bind(null, false)}
        defaultDates={
          rangeFromStor?.type === 'r'
            ? {
                start: rangeFromStor.parameters.find((p) => p.condition === '>')?.value,
                end: rangeFromStor.parameters.find((p) => p.condition === '<')?.value,
              }
            : undefined
        }
      />
    </div>
  );
};
